import { ref, computed } from 'vue'
import { api } from '@general/ui-kit'

import { BRAND_STORAGE_KEY, PRODUCT_NAME, brandStorageKey, readStoredBrand, brandView } from './lib/brandState.mjs'

/**
 * The instance's brand in the browser: what the app is called and what its
 * icon looks like (backend/brand.js works it out from the company profile).
 *
 * Not a pinia store, on purpose. Every store is reset when a session ends
 * (platform/web main.js), and the brand is not the session's: the sign-in
 * screen the next person sees is the client's too.
 *
 * Kept in localStorage under the key index.html reads before the first paint
 * — change one, change the other — so the second visit opens with the
 * client's name, initials and logo from the first frame, without waiting for
 * a request. Only the public brand is kept (a few hundred bytes): the logo is
 * a versioned URL (`/brand/logo?v=`) that the HTTP cache keeps for a year.
 */

export { BRAND_STORAGE_KEY, PRODUCT_NAME }

const BASE = import.meta.env?.BASE_URL ?? '/'
const STORAGE_KEY = brandStorageKey(BASE)

/** How long the corner waits for an answer before giving up on a first visit. */
export const BRAND_TIMEOUT_MS = 8000

function storage() {
  try { return typeof localStorage === 'undefined' ? null : localStorage } catch { return null }
}

export const brand = ref(readStoredBrand(storage(), STORAGE_KEY))

/** True once this page load has an answer from the API, or has given up on one. */
export const brandSettled = ref(false)

/** The app's name, or the product's while nothing is known. */
export const brandName = computed(() => brand.value?.name || PRODUCT_NAME)

/** Nothing known yet — no copy from an earlier visit and no answer: a skeleton, not a guess. */
export const brandPending = computed(() => !brand.value && !brandSettled.value)

/**
 * What a brand corner paints (BrandMark.vue): skeleton, picture or initials.
 * `logo` is the company logo (the menu), `icon` the installed app's icon (the door).
 */
export function useBrandView(kind) {
  return computed(() => brandView(brand.value, { settled: brandSettled.value, kind, asset: brandAsset }))
}

/**
 * The address of one of the brand's pictures, on the app's OWN origin and
 * under its base: the icons are links of this page (favicon, apple-touch-icon)
 * and an instance served under a sub-path has them under it. With the version,
 * so a browser can keep it for a year and still never show an old one.
 */
export function brandAsset(file, version = brand.value?.version) {
  return `${BASE}api/v1/brand/${file}${version ? `?v=${encodeURIComponent(version)}` : ''}`
}

// -- Small colour helpers ----------------------------------------------------
// Enough to derive hover and tint shades from one accent colour without pulling
// in a colour library.

function parseHex(hex) {
  return [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16))
}

function shade(hex, amount) {
  const channels = parseHex(hex).map(value => {
    const target = amount < 0 ? 0 : 255
    return Math.round(value + (target - value) * Math.abs(amount))
  })
  return `#${channels.map(value => value.toString(16).padStart(2, '0')).join('')}`
}

function withAlpha(hex, alpha) {
  const [r, g, b] = parseHex(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * The accent colour becomes --color-primary and its shades. Here and not only
 * in the company store, because the sign-in screen is painted before the
 * profile can be read: with only the store, its button was the product's blue
 * on every client's door.
 */
export function applyAccent(hex) {
  if (typeof document === 'undefined' || !/^#[0-9a-fA-F]{6}$/.test(hex ?? '')) return
  const root = document.documentElement.style
  root.setProperty('--color-primary', hex)
  root.setProperty('--color-primary-dark', shade(hex, -0.14))
  root.setProperty('--color-primary-light', shade(hex, 0.14))
  root.setProperty('--color-primary-soft', withAlpha(hex, 0.10))
  root.setProperty('--color-primary-ring', withAlpha(hex, 0.25))
}

/** Puts the brand on the page: the icons, the iOS name, the browser bar, the accent. */
export function applyBrand(value = brand.value) {
  if (!value || typeof document === 'undefined') return

  applyAccent(value.accent)

  for (const link of document.querySelectorAll('link[data-brand]')) {
    const href = brandAsset(link.getAttribute('data-brand'), value.version)
    if (link.getAttribute('href') !== href) link.setAttribute('href', href)
  }

  // What iOS writes under the icon when somebody adds the app to the home
  // screen — it reads the page at that moment, so the page has to be right.
  const title = document.querySelector('meta[name="apple-mobile-web-app-title"]')
  if (title && value.shortName) title.setAttribute('content', value.shortName)

  if (/^#[0-9a-f]{6}$/i.test(value.color ?? '')) {
    // Read from here by the ui-kit's useTheme, which owns the bar from now on.
    document.documentElement.dataset.brandColor = value.color
    // A dark theme keeps its dark bar; a light one takes the client's colour.
    if (document.documentElement.dataset.theme !== 'dark') {
      for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
        meta.setAttribute('content', value.color)
      }
    }
  }
}

function remember(value) {
  try { storage()?.setItem(STORAGE_KEY, JSON.stringify(value)) } catch { /* private mode, full */ }
}

let pending = null

/**
 * Asks the API for the brand — no session needed: it is what the sign-in
 * screen paints — and applies it. Never throws: without an answer the page
 * keeps whatever it had, the last copy or the product's. Never hangs either:
 * after BRAND_TIMEOUT_MS it gives up, so a first visit on a dead network
 * falls back to the initials instead of shimmering for ever.
 *
 * @param {{ force?: boolean }} [options]  force: ask again even if a request is in flight
 */
export function loadBrand({ force = false } = {}) {
  if (pending && !force) return pending
  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = controller ? setTimeout(() => controller.abort(), BRAND_TIMEOUT_MS) : null
  const request = fetch(api.url('/brand'), { cache: 'no-cache', signal: controller?.signal })
    .then(response => (response.ok ? response.json() : null))
    .then((body) => {
      const value = body?.data?.brand
      if (!value || typeof value !== 'object') return brand.value
      brand.value = value
      remember(value)
      applyBrand(value)
      return value
    })
    .catch(() => brand.value)
    .finally(() => {
      clearTimeout(timer)
      brandSettled.value = true
      if (pending === request) pending = null
    })
  pending = request
  return request
}
